// src/components/AdminDashboard.js
import { useState, useEffect } from 'react';
import {
  getUsers,
  getProperties,
  approveUser,
  getPendingApprovals,
  approveProperty,
  createUser,
  updateUser,
  deleteUser,
  uploadJson,
} from '../api';

const CITIES = ['mumbai', 'pune', 'delhi', 'bangalore'];
const ROLES = ['admin', 'team_lead', 'rep', 'broker', 'client'];
const ROLE_LABELS = {
  admin: 'Admin',
  team_lead: 'Team Lead',
  rep: 'Treta Sales Rep',
  broker: 'Broker',
  client: 'Client',
};

function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);

  // Create user form
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    role: 'client',
    status: 'approved',
    cities: ['mumbai'],
  });

  // Edit user modal
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    email: '',
    password: '',
    role: '',
    status: '',
    cities: [],
  });

    // ✅ NEW: Role management modal
  const [rolesModalUser, setRolesModalUser] = useState(null);   // User being managed
  const [userRoles, setUserRoles] = useState([]);               // Their current roles
  const [rolesLoading, setRolesLoading] = useState(false);
  const [newRoleData, setNewRoleData] = useState({
    role: 'team_lead',
    cities: ['mumbai'],
  });

  // Upload JSON
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');

  // Property search & pagination
  const [propertySearch, setPropertySearch] = useState('');
  const [propertyPage, setPropertyPage] = useState(1);
  const PROPERTIES_PER_PAGE = 20;

  // ===== HELPERS =====
  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 5000);
  };


    // ✅ NEW: Open role management modal
  const handleOpenRolesModal = async (user) => {
    setRolesModalUser(user);
    setRolesLoading(true);
    setNewRoleData({ role: 'team_lead', cities: ['mumbai'] });

    try {
      const { getUserRoles } = require('../api');
      const data = await getUserRoles(user._id);
      setUserRoles(data.roles || []);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to load roles');
    } finally {
      setRolesLoading(false);
    }
  };

  const handleCloseRolesModal = () => {
    setRolesModalUser(null);
    setUserRoles([]);
  };

  // ✅ NEW: Add a role
  const handleAddRole = async () => {
    if (!rolesModalUser) return;
    try {
      const { addUserRole } = require('../api');
      await addUserRole(rolesModalUser._id, newRoleData.role, newRoleData.cities);
      showSuccess(`✅ Role "${newRoleData.role}" added`);

      // Refresh roles
      const { getUserRoles } = require('../api');
      const data = await getUserRoles(rolesModalUser._id);
      setUserRoles(data.roles || []);

      // Reset form
      setNewRoleData({ role: 'team_lead', cities: ['mumbai'] });

      // Refresh user list so effectiveRoles reflect change
      const usersResponse = await getUsers();
      setUsers(usersResponse || []);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to add role');
    }
  };

  // ✅ NEW: Revoke a role
  const handleRevokeRole = async (roleId, roleName) => {
    if (!rolesModalUser) return;
    if (!window.confirm(`Revoke role "${roleName}"?`)) return;

    try {
      const { revokeUserRole } = require('../api');
      await revokeUserRole(rolesModalUser._id, roleId);
      showSuccess(`✅ Role "${roleName}" revoked`);

      // Refresh
      const { getUserRoles } = require('../api');
      const data = await getUserRoles(rolesModalUser._id);
      setUserRoles(data.roles || []);

      // Refresh user list
      const usersResponse = await getUsers();
      setUsers(usersResponse || []);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to revoke role');
    }
  };
  // ===== DATA FETCHING =====
  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersResponse, propertiesResponse] = await Promise.all([
        getUsers(),
        getPendingApprovals({ status: 'pending', limit: 500 }),  
      ]);
      setUsers(usersResponse || []);
      setProperties(propertiesResponse?.properties || propertiesResponse || []);
      setError('');
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch data';
      showError(errorMessage);
      if (err.response?.status === 401) {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ===== USER ACTIONS =====
  const handleApproveUser = async (id, email) => {
    try {
      await approveUser(id);
      setUsers(users.map((user) => (user._id === id ? { ...user, status: 'approved' } : user)));
      showSuccess(`✅ User approved: ${email}`);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to approve user');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await createUser(newUser);
      showSuccess(`✅ User created: ${newUser.email}`);
      setNewUser({
        email: '',
        password: '',
        role: 'client',
        status: 'approved',
        cities: ['mumbai'],
      });
      const usersResponse = await getUsers();
      setUsers(usersResponse || []);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleOpenEditUser = (user) => {
    setEditingUser(user);
    setEditFormData({
      email: user.email,
      password: '',  // Leave blank to keep current
      role: user.role,
      status: user.status,
      cities: user.cities || ['mumbai'],
    });
  };

  const handleCloseEditUser = () => {
    setEditingUser(null);
    setEditFormData({ email: '', password: '', role: '', status: '', cities: [] });
  };

  const handleSaveEditUser = async () => {
    try {
      await updateUser(editingUser._id, editFormData);
      showSuccess(`✅ User updated: ${editFormData.email}`);
      handleCloseEditUser();
      const usersResponse = await getUsers();
      setUsers(usersResponse || []);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (id, email) => {
    if (!window.confirm(`Delete user "${email}"? This cannot be undone.`)) return;
    try {
      await deleteUser(id);
      setUsers(users.filter((user) => user._id !== id));
      showSuccess(`🗑️ User deleted: ${email}`);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  
  // ===== PROPERTY ACTIONS =====
  const handleApproveProperty = async (id, location) => {
    try {
      await approveProperty(id);
      setProperties(properties.filter((prop) => prop._id !== id));
      showSuccess(`✅ Property approved: ${location}`);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to approve property');
    }
  };

  // ===== UPLOAD JSON =====
  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      setUploadStatus('Please select a JSON file');
      return;
    }
    try {
      const response = await uploadJson(uploadFile);
      setUploadStatus(response.message);
      setUploadFile(null);
      const propertiesResponse = await getPendingApprovals({ status: 'pending', limit: 500 });
      setProperties(propertiesResponse?.properties || propertiesResponse || []);
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message || 'Failed to upload file';
      setUploadStatus(`${message}${err.response?.data?.details ? `: ${err.response.data.details}` : ''}`);
    }
  };

  // ===== PROPERTY FILTERING & PAGINATION =====
  const filteredProperties = properties.filter((p) => {
    if (!propertySearch.trim()) return true;
    const s = propertySearch.toLowerCase();
    return (
      (p.location || '').toLowerCase().includes(s) ||
      (p.sub_locality || '').toLowerCase().includes(s) ||
      (p.property_type || '').toLowerCase().includes(s)
    );
  });

  const totalPropertyPages = Math.ceil(filteredProperties.length / PROPERTIES_PER_PAGE);
  const paginatedProperties = filteredProperties.slice(
    (propertyPage - 1) * PROPERTIES_PER_PAGE,
    propertyPage * PROPERTIES_PER_PAGE
  );

  // ===== STATS =====
  const pendingUsersCount = users.filter((u) => u.status === 'pending').length;
  const totalUsersCount = users.length;
  const pendingPropertiesCount = properties.length;

  // ===== RENDER =====
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">⚙️ Admin Dashboard</h1>
        <button
          onClick={fetchData}
          className="bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded text-sm"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Success/Error banners */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* ===== SUMMARY CARDS ===== */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-sm text-blue-600 font-medium">Total Users</div>
          <div className="text-3xl font-bold text-blue-800">{totalUsersCount}</div>
          {pendingUsersCount > 0 && (
            <div className="text-xs text-orange-600 mt-1">⏳ {pendingUsersCount} pending approval</div>
          )}
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-sm text-yellow-700 font-medium">Pending Properties</div>
          <div className="text-3xl font-bold text-yellow-800">{pendingPropertiesCount}</div>
          <div className="text-xs text-gray-600 mt-1">Awaiting review</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-sm text-green-600 font-medium">System Status</div>
          <div className="text-lg font-bold text-green-800">✅ All Good</div>
          <div className="text-xs text-gray-600 mt-1">Last refresh: just now</div>
        </div>
      </div>

      {/* ===== UPLOAD JSON (collapsible) ===== */}
      <details className="mb-6 bg-white rounded-lg shadow-sm">
        <summary className="cursor-pointer p-4 font-bold text-gray-700 hover:bg-gray-50 rounded-lg">
          📥 Upload JSON Data (Bulk Import)
        </summary>
        <div className="p-4 border-t">
          <form onSubmit={handleFileUpload} className="flex flex-col sm:flex-row gap-2">
            <input
              type="file"
              accept=".json"
              onChange={(e) => setUploadFile(e.target.files[0])}
              className="border p-2 rounded text-sm flex-1"
            />
            <button
              type="submit"
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded text-sm"
            >
              Upload
            </button>
          </form>
          {uploadStatus && (
            <p className={`text-sm mt-2 ${uploadStatus.toLowerCase().includes('fail') ? 'text-red-500' : 'text-green-600'}`}>
              {uploadStatus}
            </p>
          )}
        </div>
      </details>

      {/* ===== CREATE USER ===== */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <h3 className="font-bold text-lg text-gray-800 mb-3">👤 Create User</h3>
        <form onSubmit={handleCreateUser} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="email"
              placeholder="Email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              className="border p-2 rounded text-sm"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              className="border p-2 rounded text-sm"
              required
            />
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              className="border p-2 rounded text-sm"
              required
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
            <select
              value={newUser.status}
              onChange={(e) => setNewUser({ ...newUser, status: e.target.value })}
              className="border p-2 rounded text-sm"
              required
            >
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/* City assignment (only for team_lead, rep, broker) */}
          {['team_lead', 'rep', 'broker'].includes(newUser.role) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign Cities</label>
              <div className="flex flex-wrap gap-3">
                {CITIES.map((city) => (
                  <label key={city} className="flex items-center gap-1 text-sm capitalize">
                    <input
                      type="checkbox"
                      checked={newUser.cities.includes(city)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNewUser({
                          ...newUser,
                          cities: checked
                            ? [...newUser.cities, city]
                            : newUser.cities.filter((c) => c !== city),
                        });
                      }}
                      className="w-4 h-4"
                    />
                    {city}
                  </label>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded text-sm font-medium"
          >
            Create User
          </button>
        </form>
      </div>

      {/* ===== USERS TABLE ===== */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <h3 className="font-bold text-lg text-gray-800 mb-3">
          👥 Users ({users.length})
          {pendingUsersCount > 0 && (
            <span className="text-sm text-orange-600 ml-2">({pendingUsersCount} pending)</span>
          )}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Role</th>
                <th className="text-left p-2">Cities</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Created</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-4 text-center text-gray-500">
                    No users yet.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id} className="border-b hover:bg-gray-50">
                    <td className="p-2">{user.email}</td>
                    <td className="p-2">
                      <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </td>
                    <td className="p-2 text-xs text-gray-600">
                      {user.cities?.length ? user.cities.join(', ') : '—'}
                    </td>
                    <td className="p-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        user.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="p-2 text-xs text-gray-600">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                                        <td className="p-2">
                      <div className="flex gap-1 flex-wrap">
                        {user.status === 'pending' && (
                          <button
                            onClick={() => handleApproveUser(user._id, user.email)}
                            className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs"
                          >
                            ✅ Approve
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenEditUser(user)}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                        >
                          ✏️ Edit
                        </button>
                        {/* ✅ NEW: Manage Roles button */}
                        <button
                          onClick={() => handleOpenRolesModal(user)}
                          className="bg-purple-500 hover:bg-purple-600 text-white px-2 py-1 rounded text-xs"
                        >
                          👥 Roles
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user._id, user.email)}
                          className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== PENDING PROPERTIES ===== */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-3 gap-2">
          <h3 className="font-bold text-lg text-gray-800">
            🏢 Pending Properties ({properties.length})
          </h3>
          <input
            type="text"
            placeholder="🔍 Search by location, type..."
            value={propertySearch}
            onChange={(e) => {
              setPropertySearch(e.target.value);
              setPropertyPage(1);
            }}
            className="border p-2 rounded text-sm w-full md:w-64"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left p-2">Location</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Price</th>
                <th className="text-left p-2">Submitted</th>
                <th className="text-left p-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProperties.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-4 text-center text-gray-500">
                    {propertySearch ? 'No properties match your search.' : 'No pending properties. 🎉'}
                  </td>
                </tr>
              ) : (
                paginatedProperties.map((prop) => (
                  <tr key={prop._id} className="border-b hover:bg-gray-50">
                    <td className="p-2">{prop.location}</td>
                    <td className="p-2">
                      <span className="text-xs">{prop.property_type}</span>
                      {['Warehouse Rent', 'Warehouse Sale', 'Industrial Land', 'Factory', 'Godown', 'Cold Storage', 'Logistics Park'].includes(prop.property_type) && (
                        <span className="ml-1 text-xs bg-orange-100 text-orange-700 px-1 rounded">🏭</span>
                      )}
                    </td>
                    <td className="p-2 text-xs">{prop.price}</td>
                    <td className="p-2 text-xs text-gray-500">
                      {prop.created_at ? new Date(prop.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => handleApproveProperty(prop._id, prop.location)}
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs"
                      >
                        ✅ Approve
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPropertyPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-4">
            <button
              onClick={() => setPropertyPage(Math.max(1, propertyPage - 1))}
              disabled={propertyPage <= 1}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm disabled:opacity-50"
            >
              ← Prev
            </button>
            <span className="text-sm text-gray-600">
              Page {propertyPage} of {totalPropertyPages}
            </span>
            <button
              onClick={() => setPropertyPage(Math.min(totalPropertyPages, propertyPage + 1))}
              disabled={propertyPage >= totalPropertyPages}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm disabled:opacity-50"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* ===== EDIT USER MODAL ===== */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">✏️ Edit User</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className="border p-2 rounded w-full text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password <span className="text-xs text-gray-500">(leave blank to keep current)</span>
                </label>
                <input
                  type="password"
                  value={editFormData.password}
                  onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                  className="border p-2 rounded w-full text-sm"
                  placeholder="••••••••"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={editFormData.role}
                    onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                    className="border p-2 rounded w-full text-sm"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                    className="border p-2 rounded w-full text-sm"
                  >
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>

              {['team_lead', 'rep', 'broker'].includes(editFormData.role) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cities</label>
                  <div className="flex flex-wrap gap-3">
                    {CITIES.map((city) => (
                      <label key={city} className="flex items-center gap-1 text-sm capitalize">
                        <input
                          type="checkbox"
                          checked={editFormData.cities.includes(city)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setEditFormData({
                              ...editFormData,
                              cities: checked
                                ? [...editFormData.cities, city]
                                : editFormData.cities.filter((c) => c !== city),
                            });
                          }}
                          className="w-4 h-4"
                        />
                        {city}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-6 justify-end">
              <button
                onClick={handleCloseEditUser}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEditUser}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded text-sm"
              >
                💾 Save Changes
              </button>

            </div>
          </div>
        </div>
      )}

      {/* ===== MANAGE ROLES MODAL ===== */}
      {rolesModalUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold">👥 Manage Roles</h3>
                <p className="text-sm text-gray-600 mt-1">{rolesModalUser.email}</p>
              </div>
              <button
                onClick={handleCloseRolesModal}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ✕
              </button>
            </div>

            {/* Current roles */}
            <div className="mb-6">
              <h4 className="font-bold text-sm text-gray-700 mb-2">Current Roles</h4>
              {rolesLoading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : userRoles.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No roles assigned</p>
              ) : (
                <div className="space-y-2">
                  {userRoles.map((ur, index) => (
                    <div
                      key={ur._id || `legacy-${index}`}
                      className="flex justify-between items-center bg-gray-50 rounded p-3"
                    >
                      <div>
                        <div className="font-medium text-sm">
                          {ROLE_LABELS[ur.role] || ur.role}
                          {ur.isLegacy && (
                            <span className="ml-2 text-xs text-orange-600">(legacy)</span>
                          )}
                        </div>
                        {ur.cities?.length > 0 && (
                          <div className="text-xs text-gray-500 capitalize mt-0.5">
                            📍 {ur.cities.join(', ')}
                          </div>
                        )}
                      </div>
                      {!ur.isLegacy && (
                        <button
                          onClick={() => handleRevokeRole(ur._id, ur.role)}
                          className="text-red-500 hover:text-red-700 text-xs font-medium"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add new role */}
            <div className="border-t pt-4">
              <h4 className="font-bold text-sm text-gray-700 mb-3">Add New Role</h4>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Role</label>
                  <select
                    value={newRoleData.role}
                    onChange={(e) => setNewRoleData({ ...newRoleData, role: e.target.value })}
                    className="border rounded p-2 w-full text-sm"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                </div>

                {['team_lead', 'rep', 'broker'].includes(newRoleData.role) && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Cities</label>
                    <div className="flex flex-wrap gap-3">
                      {CITIES.map((city) => (
                        <label key={city} className="flex items-center gap-1 text-sm capitalize">
                          <input
                            type="checkbox"
                            checked={newRoleData.cities.includes(city)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setNewRoleData({
                                ...newRoleData,
                                cities: checked
                                  ? [...newRoleData.cities, city]
                                  : newRoleData.cities.filter((c) => c !== city),
                              });
                            }}
                            className="w-4 h-4"
                          />
                          {city}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleAddRole}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded text-sm font-medium"
                >
                  ➕ Add Role
                </button>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={handleCloseRolesModal}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;